import React, { useState, useEffect } from 'react';
import { Bell, X, Check, Trash2, Filter } from 'lucide-react';
import { format } from 'date-fns';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';

const NotificationCenter = () => {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [filter, setFilter] = useState('all');
  const [unreadCount, setUnreadCount] = useState(0);
  const { user } = useAuth();

  useEffect(() => {
    // Fetch notifications - Mock data for now
    fetchNotifications();
  }, [user]);

  useEffect(() => {
    setUnreadCount(notifications.filter(n => !n.read).length);
  }, [notifications]);

  const fetchNotifications = () => {
    // Mock notifications
    setNotifications([
      {
        id: 1,
        type: 'success',
        title: 'Station Online',
        message: 'Station #23 is now operational',
        timestamp: new Date(Date.now() - 1000 * 60 * 5),
        read: false,
      },
      {
        id: 2,
        type: 'warning',
        title: 'Low Uptime Alert',
        message: 'Station #45 uptime dropped below 90%',
        timestamp: new Date(Date.now() - 1000 * 60 * 30),
        read: false,
      },
      {
        id: 3,
        type: 'info',
        title: 'Report Generated',
        message: 'Monthly revenue report is ready',
        timestamp: new Date(Date.now() - 1000 * 60 * 60),
        read: true,
      },
      {
        id: 4,
        type: 'error',
        title: 'Payment Failed',
        message: 'Transaction #1234 failed',
        timestamp: new Date(Date.now() - 1000 * 60 * 120),
        read: true,
      },
    ]);
  };

  const markAsRead = (id) => {
    setNotifications(notifications.map(n =>
      n.id === id ? { ...n, read: true } : n
    ));
  };

  const markAllAsRead = () => {
    setNotifications(notifications.map(n => ({ ...n, read: true })));
  };

  const deleteNotification = (id) => {
    setNotifications(notifications.filter(n => n.id !== id));
  };

  const clearAll = () => {
    if (window.confirm('Clear all notifications?')) {
      setNotifications([]);
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'success': return 'var(--color-success)';
      case 'warning': return 'var(--color-warning)';
      case 'error': return 'var(--color-danger)';
      default: return 'var(--color-info)';
    }
  };

  const filteredNotifications = filter === 'all'
    ? notifications
    : filter === 'unread'
    ? notifications.filter(n => !n.read)
    : notifications.filter(n => n.type === filter);

  return (
    <>
      {/* Bell Icon */}
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 rounded-lg transition-colors"
        style={{
          backgroundColor: open ? 'var(--bg-hover)' : 'transparent',
        }}
        aria-label="Notifications"
      >
        <Bell size={20} style={{ color: 'var(--text-primary)' }} />
        {unreadCount > 0 && (
          <span
            className="absolute -top-1 -right-1 w-5 h-5 rounded-full text-xs flex items-center justify-center font-medium"
            style={
{
              backgroundColor: 'var(--color-danger)',
              color: 'white',
            }}
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Notification Panel */}
      <AnimatePresence>
        {open && (
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 z-40"
              onClick={() => setOpen(false)}
            />

            {/* Panel */}
            <motion.div
              initial={{ opacity: 0, x: 300 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 300 }}
              className="fixed top-16 right-4 w-96 max-h-[600px] rounded-lg border shadow-xl z-50 overflow-hidden flex flex-col"
              style={{
                backgroundColor: 'var(--bg-primary)',
                borderColor: 'var(--border-primary)',
              }}
            >
              {/* Header */}
              <div className="p-4 border-b" style={{ borderColor: 'var(--border-primary)' }}>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold" style={{ color: 'var(--text-primary)' }}>
                    Notifications
                  </h3>
                  <button
                    onClick={() => setOpen(false)}
                    className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800"
                  >
                    <X size={18} style={{ color: 'var(--text-secondary)' }} />
                  </button>
                </div>

                {/* Filters */}
                <div className="flex gap-2">
                  {['all', 'unread', 'success', 'warning', 'error'].map(f => (
                    <button
                      key={f}
                      onClick={() => setFilter(f)}
                      className="px-2 py-1 text-xs rounded capitalize"
                      style={{
                        backgroundColor: filter === f ? 'var(--color-primary)' : 'var(--bg-tertiary)',
                        color: filter === f ? 'white' : 'var(--text-secondary)',
                      }}
                    >
                      {f}
                    </button>
                  ))}
                </div>
              </div>

              {/* Actions */}
              {notifications.length > 0 && (
                <div className="px-4 py-2 border-b flex gap-2" style={{ borderColor: 'var(--border-primary)' }}>
                  <button
                    onClick={markAllAsRead}
                    disabled={unreadCount === 0}
                    className="text-xs px-2 py-1 rounded transition-colors disabled:opacity-50"
                    style={{
                      backgroundColor: 'var(--bg-tertiary)',
                      color: 'var(--text-secondary)',
                    }}
                  >
                    Mark all read
                  </button>
                  <button
                    onClick={clearAll}
                    className="text-xs px-2 py-1 rounded transition-colors"
                    style={{
                      backgroundColor: 'var(--color-danger-light)',
                      color: 'var(--color-danger)',
                    }}
                  >
                    Clear all
                  </button>
                </div>
              )}

              {/* Notifications List */}
              <div className="flex-1 overflow-y-auto">
                {filteredNotifications.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-64">
                    <Bell size={48} style={{ color: 'var(--text-tertiary)' }} className="mb-2" />
                    <p style={{ color: 'var(--text-tertiary)' }}>No notifications</p>
                  </div>
                ) : (
                  <div>
                    {filteredNotifications.map(notification => (
                      <div
                        key={notification.id}
                        className="p-4 border-b transition-colors"
                        style={{
                          backgroundColor: notification.read ? 'transparent' : 'var(--bg-secondary)',
                          borderColor: 'var(--border-primary)',
                        }}
                      >
                        <div className="flex items-start gap-3">
                          <div
                            className="w-2 h-2 rounded-full mt-2"
                            style={{ backgroundColor: getTypeColor(notification.type) }}
                          />
                          
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
                              {notification.title}
                            </h4>
                            <p className="text-sm mb-2" style={{ color: 'var(--text-secondary)' }}>
                              {notification.message}
                            </p>
                            <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                              {format(notification.timestamp, 'MMM dd, HH:mm')}
                            </span>
                          </div>

                          <div className="flex gap-1">
                            {!notification.read && (
                              <button
                                onClick={() => markAsRead(notification.id)}
                                className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800"
                                title="Mark as read"
                              >
                                <Check size={16} style={{ color: 'var(--color-success)' }} />
                              </button>
                            )}
                            <button
                              onClick={() => deleteNotification(notification.id)}
                              className="p-1 rounded hover:bg-red-100 dark:hover:bg-red-900/20"
                              title="Delete"
                            >
                              <Trash2 size={16} style={{ color: 'var(--color-danger)' }} />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default NotificationCenter;